import {Component} from 'panel';
import uniqBy from 'lodash/uniqBy';
import keyBy from 'lodash/keyBy';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {NAVIGATION_KEY_CODES} from 'common/util/dom';
import {defaultOrdering, lexicalCompose, mapArguments} from 'common/util/function';
import {stringFilterMatches} from 'common/util/string';

import css from './index.styl?inline';
import template from './index.jade';

const PROGRESSIVE_LIST_BUFFER_PX = 250;
const MAX_SPECIFY_VALUE_LENGTH = 255;
const MAX_CSV_MATCHES = 5000;
const EVENTS = {
  CLICK_ITEM: `clickItem`, // triggered when an item is clicked
  DRAW: `draw`, // when component is patched, its height could change. draw event lets clients respond accordingly
  FOCUS_ITEM: `focusItem`, // triggered when an item is focused
  SELECT_ALL: `selectAll`, // triggered when user clicks on the top box to select all items
  UNSELECT_ALL: `unselectAll`, // triggered when user clicks on a selected top box to unselect all items
  SELECT_ALL_MATCHING: `selectAllMatching`, // triggered when user clicks on the top box to select all matching items when search is present
};

export class CheckboxList extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      items: {type: `json`, default: []},
      'num-columns': `number`,
      progressive: `boolean`,
      'search-filter': `string`,
      'show-select-all': `boolean`,
      'show-csv-matches': {
        type: `boolean`,
        description: `Displays select all matching and enables CSV matching`,
      },
    };
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template,
      defaultState: {
        focusedItemIndex: 0,
        isProgressive: false,
        items: [],
        keyedItems: {},
        progressiveListSize: 20,
        searchFilter: ``,
        showSelectAll: true,
        numColumns: 1,
      },
      helpers: {
        getItemsToDisplay: this.getItemsToDisplay.bind(this),
        allSelected: () => this.allSelected,
        // Find at least 1 unselected item and revert value
        allMatchingItemsSelected: () => !this.getMatchingItems().some((item) => !item.isSelected),
        toggleSelectAllMatching: () => {
          const matchingItems = this.getMatchingItems().filter((item) => !item.isControlItem);
          this.dispatchItemsSelectionValue(matchingItems, !this.helpers.allMatchingItemsSelected());
        },
        toggleSelectAll: () => {
          const selectItems = !this.allSelected;
          const items = this.state.items.map((item) => ({...item, isSelected: selectItems}));
          this.update({items});
          if (selectItems) {
            this.dispatchEvent(new CustomEvent(EVENTS.SELECT_ALL));
          } else {
            this.dispatchEvent(new CustomEvent(EVENTS.UNSELECT_ALL));
          }
        },
        getTotalMatchingItems: () => (this.state.searchFilter ? this.getMatchingItems().length : 0),
        handleClickItem: (item) => this.toggleSelectItem(item),
        handlePostPatch: () => {
          this.dispatchEvent(new CustomEvent(EVENTS.DRAW));
        },
        handleMouseenterItem: (item) => {
          this.focusItem(item.index);
        },
      },
    };
  }

  constructor() {
    super(...arguments);
    this.handleKeydown = (ev) => {
      const {UP_ARROW, DOWN_ARROW, ENTER} = NAVIGATION_KEY_CODES;
      const {keyCode} = ev;

      if (keyCode === ENTER || keyCode === UP_ARROW || keyCode === DOWN_ARROW) {
        ev.stopPropagation();
        ev.preventDefault();
      }

      if (keyCode === ENTER) {
        this.toggleSelectItem(this.getFocusedItem());
      } else if (keyCode === UP_ARROW) {
        this.focusItem(Math.max(this.state.focusedItemIndex - 1, 0));
        window.requestAnimationFrame(() => {
          this.scrollToItemIfNeeded();
        });
      } else if (keyCode === DOWN_ARROW) {
        this.focusItem(Math.min(this.state.focusedItemIndex + 1, this.state.items.length - 1));
        window.requestAnimationFrame(() => {
          this.scrollToItemIfNeeded();
        });
      }
    };
  }

  connectedCallback() {
    super.connectedCallback(...arguments);

    this.handleScroll = (ev) => {
      const scrollBottom = ev.target.scrollTop + ev.target.offsetHeight;
      if (this.state.isProgressive && scrollBottom + PROGRESSIVE_LIST_BUFFER_PX >= ev.target.scrollHeight) {
        this.increaseProgressiveListSize();
      }
    };

    this.addEventListener(`scroll`, this.handleScroll);
    this.addEventListener(`keydown`, this.handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback(...arguments);
    this.removeEventListener(`scroll`, this.handleScroll);
    this.removeEventListener(`keydown`, this.handleKeydown);
    if (this._keydownProxyEl) {
      this._keydownProxyEl.removeEventListener(`keydown`, this.handleKeydown);
    }
  }

  cacheItemsByKey(items) {
    let keyedItems = {};
    try {
      keyedItems = keyBy(items, (item) => item.label.toLowerCase().trim());
    } catch (err) {
      console.error(`Error memoizing list items: `, err);
    }
    return keyedItems;
  }

  attributeChangedCallback(attr) {
    super.attributeChangedCallback(...arguments);
    switch (attr) {
      case `items`:
        this.update({
          items: this.attr(attr),
          keyedItems: this.cacheItemsByKey(this.attr(attr)),
        });
        break;
      case `progressive`:
        this.update({isProgressive: this.attr(attr)});
        break;
      case `search-filter`:
        this.update({searchFilter: this.attr(attr)});
        break;
      case `show-select-all`:
        this.update({showSelectAll: this.attr(attr)});
        break;
      case `num-columns`: {
        const numColumns = this.attr(attr);
        if (isNaN(numColumns) || numColumns < 1 || numColumns > 4) {
          throw new Error(`num-columns should be set between 1 and 4`);
        } else {
          this.update({numColumns});
        }
        break;
      }
      default:
        break;
    }
  }

  get allSelected() {
    return this.state.items.length && this.state.items.every((item) => item.isSelected);
  }

  getMatchingItemsFromCSVSearch() {
    let matches = [];
    const {searchFilter, keyedItems} = this.state;

    if (this.attr(`show-csv-matches`) && searchFilter.includes(`,`)) {
      const tokens = searchFilter.split(`,`);
      if (tokens.length <= MAX_CSV_MATCHES) {
        matches = tokens
          .map((t) => {
            const trimmed = t.trim();
            if (trimmed) {
              const token = trimmed.toLowerCase();
              return keyedItems[token] || {label: trimmed, value: trimmed, isSelected: false};
            } else {
              return false;
            }
          })
          .filter(Boolean); // Filters out empty strings and any other falsey value
      }
    }

    return matches;
  }

  getMatchingItems() {
    const {searchFilter, items} = this.state;
    const listMatches = this.getMatchingItemsFromCSVSearch();
    return [...this.filterItems(searchFilter, items), ...listMatches].map((item, index) => ({
      ...item,
      index,
    }));
  }

  getItemsToDisplay() {
    const {searchFilter, items, isProgressive, progressiveListSize} = this.state;
    const listMatches = this.getMatchingItemsFromCSVSearch();
    const matchedItems = this.getMatchingItems();
    // Value should always be present on an item but if it isn't default to label
    let processedItems = uniqBy(matchedItems, (item) => item.value || item.label);

    if (isProgressive && !listMatches.length) {
      processedItems = processedItems.slice(0, progressiveListSize);
    }

    if (searchFilter) {
      const hasExactMatch = items.some((item) => item.label === searchFilter);
      if (!hasExactMatch && searchFilter.length <= MAX_SPECIFY_VALUE_LENGTH) {
        // allow user to specify equals value even if it's not in the list
        processedItems.push({
          index: processedItems.length,
          label: `Specify: ${searchFilter}`,
          value: searchFilter,
          isSelected: false,
          // Indicates that this item is a control and is not
          // provided by a parent component
          isControlItem: true,
        });
      }
    }

    return processedItems;
  }

  filterItems(searchFilter, items) {
    if (!searchFilter) {
      return items;
    }

    return items
      .map((item) => ({...item, matches: stringFilterMatches(item.label, searchFilter)}))
      .filter((item) => !!item.matches)
      .sort(
        lexicalCompose(
          // prioritize beginning match
          mapArguments(defaultOrdering, (item) => -item.matches[0].length),
          // second: a-z
          mapArguments(defaultOrdering, (item) => item.label.toLowerCase()),
        ),
      );
  }

  dispatchItemsSelectionValue(items, value) {
    this.dispatchEvent(
      new CustomEvent(EVENTS.SELECT_ALL_MATCHING, {
        detail: {
          items: items.map((item) => ({...item, isSelected: value})),
        },
      }),
    );
  }

  toggleSelectItem(item) {
    const selectedItem = {...item, isSelected: !item.isSelected};
    this.dispatchEvent(new CustomEvent(EVENTS.CLICK_ITEM, {detail: {item: selectedItem}}));
  }

  focusItem(focusedItemIndex) {
    this.update({focusedItemIndex});
    this.dispatchEvent(new CustomEvent(EVENTS.FOCUS_ITEM, {detail: {item: this.getFocusedItem()}}));
  }

  increaseProgressiveListSize() {
    if (this.state.progressiveListSize < this.state.items.length) {
      const progressiveListSize = this.state.progressiveListSize * 2;
      this.update({progressiveListSize});
    }
  }

  getFocusedItem() {
    return this.getItemsToDisplay().find((item) => item.index === this.state.focusedItemIndex);
  }

  scrollToItemIfNeeded() {
    const listEl = this;
    const focusedEl = this.el.querySelector(`.mp-checkbox-list-focused`);
    if (!focusedEl) {
      return;
    }

    // The top threshold should be large enough to account for section header
    const THRESHOLD_TOP = 45;
    const THRESHOLD_BOTTOM = 45;
    const listElTop = listEl.scrollTop;
    const listElBottom = listElTop + listEl.offsetHeight;
    const focusedElTop = focusedEl.offsetTop - listEl.offsetTop;
    const focusedElBottom = focusedElTop + focusedEl.offsetHeight;
    if (focusedElTop - THRESHOLD_TOP < listElTop) {
      listEl.scrollTop = focusedElTop - THRESHOLD_TOP;
    } else if (focusedElBottom + THRESHOLD_BOTTOM > listElBottom) {
      listEl.scrollTop = focusedElBottom + THRESHOLD_BOTTOM - listEl.offsetHeight;
    }
  }

  set keydownProxyEl(domElement) {
    if (this._keydownProxyEl) {
      this._keydownProxyEl.removeEventListener(`keydown`, this.handleKeydown);
    }
    this._keydownProxyEl = domElement;
    this._keydownProxyEl.addEventListener(`keydown`, this.handleKeydown);
  }

  focus() {
    this.el.querySelector(`.mp-checkbox-list-list`).focus();
  }
}

export const CheckboxListEl = defineAndMakeJsxFactory(`mp-checkbox-list`, CheckboxList);
